import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import Loading from '../../components/common/Loading/Loading';
import { SharedTokenService } from '../../services/SharedTokenService';
import { CHECKOUT_SUCCESS_DATA_QUERY_PARAM } from './checkout.constants';
import type { CheckoutSuccessSnapshotData } from './checkout.types';
import CheckoutContainer from './components/CheckoutContainer';
import CheckoutHeader from './components/CheckoutHeader';
import CheckoutSuccessView from './components/CheckoutSuccessView';
import CheckoutWelcomeView from './components/CheckoutWelcomeView';
import {
  CHECKOUT_SUCCESS_INVALID_URL_MESSAGE,
  checkoutSuccessSnapshotToFormState,
  isCheckoutSuccessTokenExpired,
  parseCheckoutSuccessTokenPayload,
} from './checkoutSuccess.utils';

type CheckoutSuccessPageState =
  | { status: 'loading' }
  | { status: 'invalid' }
  | { status: 'expired' }
  | {
    status: 'ready';
    snapshot: CheckoutSuccessSnapshotData;
    message: string;
  };

const CheckoutSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [pageState, setPageState] = useState<CheckoutSuccessPageState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    const loadSuccessData = async () => {
      const token = searchParams.get(CHECKOUT_SUCCESS_DATA_QUERY_PARAM)?.trim();

      if (!token) {
        if (!cancelled) {
          setPageState({ status: 'invalid' });
        }
        return;
      }

      try {
        const decoded = await SharedTokenService.decodeCheckoutSuccess(token);
        const parsed = parseCheckoutSuccessTokenPayload(decoded.payload);

        if (!parsed) {
          if (!cancelled) {
            setPageState({ status: 'invalid' });
          }
          return;
        }

        if (isCheckoutSuccessTokenExpired(parsed.created_at)) {
          if (!cancelled) {
            setPageState({ status: 'expired' });
          }
          return;
        }

        if (!cancelled) {
          setPageState({
            status: 'ready',
            snapshot: parsed.data,
            message: parsed.data.message,
          });
        }
      } catch {
        if (!cancelled) {
          setPageState({ status: 'invalid' });
        }
      }
    };

    setPageState({ status: 'loading' });
    void loadSuccessData();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  if (pageState.status === 'loading') {
    return (
      <CheckoutContainer>
        <CheckoutHeader />
        <Loading />
      </CheckoutContainer>
    );
  }

  if (pageState.status === 'invalid' || pageState.status === 'expired') {
    return (
      <CheckoutContainer>
        <CheckoutHeader />
        <EmptyState
          message={CHECKOUT_SUCCESS_INVALID_URL_MESSAGE}
          icon="link_off"
        />
      </CheckoutContainer>
    );
  }

  const isChargeCheckout = pageState.snapshot.mode === 'charge_checkout';

  if (!isChargeCheckout) {
    return <CheckoutWelcomeView />;
  }

  const form = checkoutSuccessSnapshotToFormState(pageState.snapshot);

  return (
    <CheckoutContainer>
      <CheckoutHeader />
      <CheckoutSuccessView
        form={form}
        selectedPlan={pageState.snapshot.selectedPlan}
        message={pageState.message}
        paymentInstructions={pageState.snapshot.paymentInstructions}
        externalPaymentId={pageState.snapshot.externalPaymentId}
        mode={isChargeCheckout ? 'charge_checkout' : 'checkout'}
      />
    </CheckoutContainer>
  );
};

export default CheckoutSuccessPage;
